import type { FileMap } from '~/lib/stores/files';
import type { StreamingFile } from '~/lib/stores/streaming-file';

export interface ViewerTarget {
  path: string;
  content: string;
  animate: boolean;
  errored: boolean;
}

/** Text content of a non-binary file from the files map, else undefined. */
function fileContent(files: FileMap, path: string | undefined): string | undefined {
  if (!path) {
    return undefined;
  }

  const dirent = files[path];

  if (!dirent || dirent.type !== 'file' || dirent.isBinary) {
    return undefined;
  }

  return dirent.content;
}

/**
 * Decide what the read-only code viewer should display.
 *
 * The editor `documents` map is not populated in the viewer-only workbench, so
 * `currentDocument` is usually undefined — the canonical fallback is the
 * FilesStore map (kept fresh by the sandbox file-watch). Without that fallback
 * every tree click rendered "No file open" even though the content was loaded.
 *
 * Streaming rules (unchanged from the original panel): the in-flight file wins
 * while it is the active selection (or nothing is selected); after the write
 * finishes, hand off to canonical content as soon as it exists so the buffer
 * never goes stale.
 */
export function resolveViewerTarget(args: {
  files: FileMap;
  selectedFile: string | undefined;
  currentDocument: { filePath: string; value: string } | undefined;
  streaming: StreamingFile | null | undefined;
}): ViewerTarget | null {
  const { files, selectedFile, currentDocument, streaming } = args;

  const streamingActive = !!streaming && (!selectedFile || selectedFile === streaming.filePath);

  if (streamingActive) {
    if (streaming.status === 'error') {
      return { path: streaming.filePath, content: streaming.content, animate: false, errored: true };
    }

    const canonical =
      streaming.status !== 'streaming' ? (currentDocument?.value ?? fileContent(files, streaming.filePath)) : undefined;

    if (canonical !== undefined) {
      return { path: streaming.filePath, content: canonical, animate: false, errored: false };
    }

    return {
      path: streaming.filePath,
      content: streaming.content,
      animate: streaming.status === 'streaming',
      errored: false,
    };
  }

  const staticContent = currentDocument?.value ?? fileContent(files, selectedFile);

  if (selectedFile && staticContent !== undefined) {
    return { path: selectedFile, content: staticContent, animate: false, errored: false };
  }

  return null;
}
